import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';
import { Dialog } from '../nursery/accounts/AccountBits';
import platformService, {
  type DeletionPerson, type OrganisationDeletion, type OrganisationDeletionPreview,
} from '../../services/platform';
import { errorMessage } from '../../utils/apiResponse';

/**
 * Delete an organisation (T&C 23.3, on the customer's request). The server's
 * preview runs the whole deletion and rolls it back, so what it lists is what
 * would really go. Deleting needs the name typed back, and a separate tick
 * when rows of other organisations would go with it; the server checks both.
 */
export default function DeleteOrganisationDialog({ orgId, orgName, orgNames, onClose, onDeleted }: {
  orgId: number;
  orgName: string;
  /** Names for the organisations people are moved to. */
  orgNames: Map<number, string>;
  onClose: () => void;
  onDeleted: (result: OrganisationDeletion) => void;
}) {
  const [preview, setPreview] = useState<OrganisationDeletionPreview | null>(null);
  const [typed, setTyped] = useState('');
  const [acceptOther, setAcceptOther] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    platformService.previewOrganisationDeletion(orgId)
      .then((res) => (res.success ? setPreview(res.data) : setError(errorMessage(res, 'Could not work out what would be deleted.'))))
      .catch((e) => setError(errorMessage(e, 'Could not work out what would be deleted.')));
  }, [orgId]);

  const otherRows = preview?.totals?.other_org_deleted ?? 0;
  const nameMatches = typed.trim() === orgName.trim();
  const canDelete = !!preview && !preview.blocked && !!preview.totals
    && nameMatches && (otherRows === 0 || acceptOther) && !busy;

  const handleDelete = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await platformService.deleteOrganisation(orgId, typed.trim(), otherRows > 0 && acceptOther);
      if (res.success) { onDeleted(res.data); return; }
      setError(errorMessage(res, 'The organisation was not deleted.'));
    } catch (e) {
      setError(errorMessage(e, 'The organisation was not deleted.'));
    }
    setBusy(false);
  };

  const movedTo = (id: number | null) => (id == null ? 'no organisation' : orgNames.get(id) ?? `organisation ${id}`);

  return (
    <Dialog
      title={`Delete ${orgName}`}
      subtitle="This cannot be undone."
      onClose={busy ? () => {} : onClose}
      wide
      footer={preview && !preview.blocked && preview.totals ? (
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Keep it</Button>
          <Button variant="danger" onClick={handleDelete} disabled={!canDelete}>
            {busy ? 'Deleting…' : 'Delete organisation'}
          </Button>
        </>
      ) : (
        <Button variant="outline" onClick={onClose}>Close</Button>
      )}
    >
      {!preview && !error && (
        <p className="text-sm text-neutral-500">Working out what would be deleted. On a large organisation this takes a while.</p>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>
      )}

      {preview?.blocked && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 space-y-1">
          <p className="font-medium">The database would refuse this deletion, so nothing can be deleted yet.</p>
          <p className="break-words">{preview.blocked.message}</p>
        </div>
      )}

      {preview && !preview.blocked && preview.people && preview.totals && preview.tables && (
        <>
          <p className="text-sm text-neutral-700">
            Deleting removes {preview.totals.deleted.toLocaleString('en-AU')} row{preview.totals.deleted === 1 ? '' : 's'}
            {preview.totals.updated > 0 && <> and changes {preview.totals.updated.toLocaleString('en-AU')} more</>}.
            {preview.approximate && ' The counts are approximate: a step had to be retried.'}
          </p>

          <PeopleList
            title={`Accounts deleted (${preview.people.deleted.length})`}
            note="These people belong only to this organisation. Their accounts are anonymised, as when someone deletes their own."
            people={preview.people.deleted}
          />
          <PeopleList
            title={`Accounts kept (${preview.people.moved.length})`}
            note="These people also belong to another organisation, or hold super admin. They keep their accounts."
            people={preview.people.moved}
            extra={(p) => `moves to ${movedTo((p as DeletionPerson & { moves_to: number | null }).moves_to)}`}
          />

          {preview.external && preview.external.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
              <p className="font-medium">Outside the app, still pointing at it</p>
              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                {preview.external.map((x) => (
                  <li key={x.table} className="break-words">{x.count} × {x.what}</li>
                ))}
              </ul>
            </div>
          )}

          {preview.left && preview.left.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-900">
              <p className="font-medium">Rows that could not be cleared</p>
              <ul className="list-disc pl-5 mt-1 space-y-0.5">
                {preview.left.map((l) => <li key={l} className="break-words">{l}</li>)}
              </ul>
            </div>
          )}

          <details className="text-sm">
            <summary className="cursor-pointer text-neutral-700">What goes, table by table ({preview.tables.length})</summary>
            <div className="mt-2 overflow-x-auto border border-neutral-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 text-neutral-600">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium">Table</th>
                    <th className="text-right px-2 py-1 font-medium">Deleted</th>
                    <th className="text-right px-2 py-1 font-medium">Changed</th>
                    <th className="text-right px-2 py-1 font-medium">Other orgs'</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {preview.tables.map((t) => (
                    <tr key={t.table} className={t.other_org_deleted ? 'bg-red-50' : undefined}>
                      <td className="px-2 py-1 font-mono">{t.table}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{t.deleted}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{t.updated}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{t.other_org_deleted ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>

          {otherRows > 0 && (
            <label className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-800">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={acceptOther}
                onChange={(e) => setAcceptOther(e.target.checked)}
              />
              <span className="min-w-0">
                <AlertTriangle className="inline h-4 w-4 mr-1 -mt-0.5" />
                This also deletes {otherRows} row{otherRows === 1 ? '' : 's'} belonging to other organisations
                (marked red in the table). I have checked them and they should go.
              </span>
            </label>
          )}

          <div>
            <label htmlFor="delete-org-confirm" className="block text-sm font-medium text-neutral-700">
              Type <span className="font-semibold break-words">{orgName}</span> to confirm
            </label>
            <input
              id="delete-org-confirm"
              type="text"
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
            />
          </div>
        </>
      )}
    </Dialog>
  );
}

function PeopleList({ title, note, people, extra }: {
  title: string;
  note: string;
  people: DeletionPerson[];
  extra?: (p: DeletionPerson) => string;
}) {
  if (people.length === 0) return null;
  return (
    <div className="text-sm">
      <p className="font-medium text-neutral-900">{title}</p>
      <p className="text-xs text-neutral-500">{note}</p>
      <ul className="mt-1 max-h-40 overflow-y-auto divide-y divide-neutral-100 border border-neutral-200 rounded-lg">
        {people.map((p) => (
          <li key={p.id} className="px-2 py-1.5 min-w-0">
            <span className="text-neutral-900 break-words">{p.name}</span>
            {p.email && <span className="block text-xs text-neutral-500 break-all">{p.email}</span>}
            {extra && <span className="block text-xs text-neutral-500 break-words">{extra(p)}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
